/**
 * Shared helpers for v2 aggregations that use agenticConversationEvents source.
 */
import { operators as o, common as c } from '@pendo/aggregations';
import { MESSAGE_TYPE_FILTERS, MESSAGE_TYPES, RAGE_PROMPT_USER_FILTER } from '../components/ai-agents.constants';
import { adjustParamsForEveryoneSegments } from './ai-agent-helpers';

// Sentinel filter value for the "None" option on the toolsUsed / modelsUsed /
// subagentsUsed multi-select filters. Selecting it matches conversations whose
// corresponding list field is empty (e.g. 0 tools used). The double-underscore
// form avoids colliding with real tool/model/subagent names from the schema.
export const NONE_FILTER_VALUE = '__none__';

export type ConversationEventsBaseParams = {
    appId: string;
    aiAgentId: string;
    timeSeries: c.TimeSeries;
    segmentId?: string | null;
    selectedAccount?: c.AccountFilterAggInput;
    blacklist?: c.BlacklistTypes;
    includeAnonymous?: string;
    selectedTrackedUseCaseIds?: string[];
    selectedAgentStepRuleIds?: string[];
    experimentVersion?: string;
};

export type ConversationEventsSourceOptions = {
    issues?: boolean;
    topics?: boolean;
    embeddings?: boolean;
    agentSteps?: boolean;
    agentStepRuleId?: string;
};

export function adjustedConversationParams<T extends ConversationEventsBaseParams> (params: T) {
    const { adjustedSegmentId, adjustedIncludeAnonymous, adjustedBlacklist } = adjustParamsForEveryoneSegments(
        params.segmentId ?? null,
        params.includeAnonymous ?? '',
        params.blacklist ?? 'apply',
    );

    return {
        ...params,
        segmentId: adjustedSegmentId,
        includeAnonymous: adjustedIncludeAnonymous,
        blacklist: adjustedBlacklist,
    };
}

function agentStepSourceParams ({ agentSteps, agentStepRuleId }: ConversationEventsSourceOptions) {
    if (!agentSteps) return {};

    return agentStepRuleId ? { agentSteps: true, agentStepRuleId } : { agentSteps: true };
}

export function baseConversationEventsPipeline (
    params: ConversationEventsBaseParams,
    sourceOptions: ConversationEventsSourceOptions = {},
): c.PipelineData[] {
    const {
        appId,
        aiAgentId,
        timeSeries,
        segmentId,
        selectedAccount,
        blacklist,
        includeAnonymous,
        selectedTrackedUseCaseIds = [],
        selectedAgentStepRuleIds,
        experimentVersion,
    } = params;

    return [
        o.sources.agenticConversationEvents({
            timeSeries,
            blacklist,
            appId,
            ...(sourceOptions.issues ? { issues: true } : {}),
            ...(sourceOptions.topics ? { topics: true } : {}),
            ...(sourceOptions.embeddings ? { embeddings: true } : {}),
            ...agentStepSourceParams(sourceOptions),
            ...(experimentVersion ? { _eventVersion: experimentVersion } : {}),
        }),
        o.identified(includeAnonymous),
        segmentId ? o.segment(segmentId) : null,
        selectedAccount ? o.accountFilter(selectedAccount) : null,
        o.filter(`agentId == "${aiAgentId}"`),
        ...trackedItemConversationScopedFilterStages({
            selectedTrackedUseCaseIds,
            selectedAgentStepRuleIds,
            timeSeries,
            appId,
            aiAgentId,
            blacklist,
            includeAnonymous,
            segmentId,
            selectedAccount,
            experimentVersion,
        }),
    ].filter(Boolean) as c.PipelineData[];
}

// Filters the outer pipeline to conversations that contain at least one matching
// TUC subrow, AND overrides each row's numPrompts with the count of TUC-matched
// prompts (the TUC subrow's `count` field = len(eventReferences) per conv).
// The conversation source has no TrackedUseCaseIds field at the parent row, so
// we can't use the V1 contains() pattern — we merge by conversationId against a
// sub-pipeline of TUC subrows. The numPrompts override keeps the overview's
// "prompts" metric semantically aligned with the TUC table: count of prompts
// that fired the selected TUCs, not total prompts in matched conversations.
// Returns [] when no TUCs are selected.
export function trackedUseCaseConversationScopedFilterStages (params: {
    selectedTrackedUseCaseIds: string[];
    timeSeries: c.TimeSeries;
    appId: string;
    aiAgentId: string;
    blacklist?: c.BlacklistTypes;
    includeAnonymous?: string;
    segmentId?: string | null;
    selectedAccount?: c.AccountFilterAggInput;
    experimentVersion?: string;
}): c.PipelineData[] {
    if (!params.selectedTrackedUseCaseIds?.length) {
        return [];
    }

    const tucIdFilter = params.selectedTrackedUseCaseIds.map((id) => `id == "${id}"`).join(' || ');

    const subPipeline = [
        o.sources.agenticConversationEvents({
            timeSeries: params.timeSeries,
            blacklist: params.blacklist,
            appId: params.appId,
            trackedUseCases: true,
            ...(params.experimentVersion ? { _eventVersion: params.experimentVersion } : {}),
        }),
        params.includeAnonymous ? o.identified(params.includeAnonymous) : null,
        params.segmentId ? o.segment(params.segmentId) : null,
        params.selectedAccount ? o.accountFilter(params.selectedAccount) : null,
        o.filter('type == "trackedusecase"'),
        o.filter(`agentId == "${params.aiAgentId}"`),
        o.filter(tucIdFilter),
        o.group(
            ['conversationId'],
            o.groupField('_matchedTuc', o.first('id')),
            o.groupField('_matchedTucEventRefs', { concat: 'eventReferences' }),
        ),
        o.evaluate({
            _matchedTucPromptCount: 'lenUnique(_matchedTucEventRefs)',
        }),
    ].filter(Boolean) as c.PipelineData[];

    return [
        o.merge(
            ['conversationId'],
            o.mappings({
                _matchedTuc: '_matchedTuc',
                _matchedTucPromptCount: '_matchedTucPromptCount',
            }),
            o.pipeline(...subPipeline),
        ),
        o.filter('!isNil(_matchedTuc)'),
    ];
}

type ConversationScopedFilterParams = {
    timeSeries: c.TimeSeries;
    appId: string;
    aiAgentId: string;
    blacklist?: c.BlacklistTypes;
    includeAnonymous?: string;
    segmentId?: string | null;
    selectedAccount?: c.AccountFilterAggInput;
    experimentVersion?: string;
};

export function agentStepConversationScopedFilterStages (
    params: ConversationScopedFilterParams & { selectedAgentStepRuleIds?: string[] },
): c.PipelineData[] {
    if (!params.selectedAgentStepRuleIds?.length) {
        return [];
    }

    const ruleIdFilter = params.selectedAgentStepRuleIds.map((id) => `ruleId == "${escapeValue(id)}"`).join(' || ');

    const subPipeline = [
        o.sources.agenticConversationEvents({
            timeSeries: params.timeSeries,
            blacklist: params.blacklist,
            appId: params.appId,
            agentSteps: true,
            ...(params.experimentVersion ? { _eventVersion: params.experimentVersion } : {}),
        }),
        params.includeAnonymous ? o.identified(params.includeAnonymous) : null,
        params.segmentId ? o.segment(params.segmentId) : null,
        params.selectedAccount ? o.accountFilter(params.selectedAccount) : null,
        o.filter('type == "agentstep"'),
        o.filter(`agentId == "${params.aiAgentId}"`),
        o.filter(ruleIdFilter),
        o.group(['conversationId'], o.groupField('_matchedAgentStepRule', o.first('ruleId'))),
    ].filter(Boolean) as c.PipelineData[];

    return [
        o.merge(
            ['conversationId'],
            o.mappings({ _matchedAgentStepRule: '_matchedAgentStepRule' }),
            o.pipeline(...subPipeline),
        ),
        o.filter('!isNil(_matchedAgentStepRule)'),
    ];
}

export function trackedItemConversationScopedFilterStages (
    params: ConversationScopedFilterParams & { selectedTrackedUseCaseIds?: string[]; selectedAgentStepRuleIds?: string[] },
): c.PipelineData[] {
    return [
        ...trackedUseCaseConversationScopedFilterStages({
            ...params,
            selectedTrackedUseCaseIds: params.selectedTrackedUseCaseIds ?? [],
        }),
        ...agentStepConversationScopedFilterStages(params),
    ];
}

// Returns the field name an agg should sum to get "TUC-matched prompts" when a
// TUC filter is active, or fall back to the conversation-level numPrompts
// otherwise. Use this at agg build time wherever the agg sums prompt counts.
export function tucMatchedNumPromptsField (selectedTrackedUseCaseIds?: string[]): string {
    return selectedTrackedUseCaseIds?.length ? '_matchedTucPromptCount' : 'numPrompts';
}

/**
 * Collapse duplicate agenticConversationEvents rows to one row per conversationId.
 * Matches AiAgentConversationsV2Aggregation list-branch dedupe so counts align with the table.
 */
export function dedupeConversationEventRowsByConversationIdStages (includeCostFields?: boolean): c.PipelineData[] {
    return [
        o.group(
            ['conversationId'],
            o.groupField('visitorId', o.first('visitorId')),
            o.groupField('accountId', o.first('accountId')),
            o.groupField('isPromptOnly', o.first('isPromptOnly')),
            o.groupField('first', { min: 'first' }),
            o.groupField('numPrompts', { max: 'numPrompts' }),
            o.groupField('_matchedTucPromptCount', { max: '_matchedTucPromptCount' }),
            o.groupField('ragePromptCount', { max: 'ragePromptCount' }),
            o.groupField('subagentsUsed', { concat: 'subagentsUsed' }),
            o.groupField('modelsUsed', { concat: 'modelsUsed' }),
            ...(includeCostFields
                ? [
                    o.groupField('conversationCost', o.first('conversationCost')),
                    o.groupField('conversationTokens', o.first('conversationTokens')),
                ]
                : []),
        ),
    ];
}

export function fileUploadFilterStages (fileUploadedFilter?: string): c.PipelineData[] {
    if (fileUploadedFilter === 'yes') return [o.filter('hasFileUploaded == true')];
    if (fileUploadedFilter === 'no') return [o.filter('hasFileUploaded == false')];

    return [];
}

export function toolFilterStages (toolId?: string): c.PipelineData[] {
    if (toolId) return [o.filter(`contains(toolsUsed, "${toolId}")`)];

    return [];
}

export function modelFilterStages (modelId?: string): c.PipelineData[] {
    if (modelId) return [o.filter(`contains(modelsUsed, "${modelId}")`)];

    return [];
}

export function subAgentFilterStages (subAgentId?: string): c.PipelineData[] {
    if (subAgentId) return [o.filter(`contains(subagentsUsed, "${subAgentId}")`)];

    return [];
}

export function userReactionFilterStages (userReactionFilter?: string): c.PipelineData[] {
    if (userReactionFilter === 'thumbsUp') return [o.filter('positiveReactionCount > 0 && negativeReactionCount == 0')];
    if (userReactionFilter === 'thumbsDown') return [o.filter('negativeReactionCount > 0 && positiveReactionCount == 0')];
    if (userReactionFilter === 'mixed') return [o.filter('positiveReactionCount > 0 && negativeReactionCount > 0')];

    return [];
}

export function usedSuggestedPromptFilterStages (usedSuggestedPromptFilter?: string): c.PipelineData[] {
    if (usedSuggestedPromptFilter === 'yes') return [o.filter('hasSuggestedPrompt == true')];
    if (usedSuggestedPromptFilter === 'no') return [o.filter('hasSuggestedPrompt == false')];

    return [];
}

export type RangeFilter = { min?: number | null; max?: number | null } | null;

export type MetadataFiltersInput = {
    userReactionFilter?: string[] | string;
    fileUploadedFilter?: string[] | string;
    usedSuggestedPromptFilter?: string[] | string;
    toolsUsedFilter?: string[];
    modelsUsedFilter?: string[];
    subagentsUsedFilter?: string[];
    inputTokenCountFilter?: RangeFilter;
    outputTokenCountFilter?: RangeFilter;
    customPropertyFilters?: Record<string, string[]>;
};

function toArray (value?: string[] | string): string[] {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.length > 0) return [value];

    return [];
}

export function escapeValue (value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function joinOr (predicates: string[]): string {
    if (predicates.length === 0) return '';
    if (predicates.length === 1) return predicates[0];

    return predicates.map((predicate) => `(${predicate})`).join(' || ');
}

function applyOrFilter (predicates: string[]): c.PipelineData[] {
    const expression = joinOr(predicates);
    if (!expression) return [];

    return [o.filter(expression)];
}

function userReactionPredicates (filter?: string[] | string): string[] {
    return toArray(filter)
        .map((value): string | null => {
            if (value === 'thumbsUp') return 'positiveReactionCount > 0 && negativeReactionCount == 0';
            if (value === 'thumbsDown') return 'negativeReactionCount > 0 && positiveReactionCount == 0';
            if (value === 'mixed') return 'positiveReactionCount > 0 && negativeReactionCount > 0';
            if (value === 'none') return 'positiveReactionCount == 0 && negativeReactionCount == 0';
            if (value === 'hasNotes') return '!isEmpty(userReactionComments)';

            return null;
        })
        .filter((predicate): predicate is string => predicate !== null);
}

function fileUploadedPredicates (filter?: string[] | string): string[] {
    return toArray(filter).map((value) => {
        if (value === 'yes') return 'hasFileUploaded == true';
        if (value === 'no') return 'hasFileUploaded == false';

        return `fileType == "${escapeValue(value)}"`;
    });
}

function usedSuggestedPromptPredicates (filter?: string[] | string): string[] {
    return toArray(filter)
        .map((value): string | null => {
            if (value === 'yes') return 'hasSuggestedPrompt == true';
            if (value === 'no') return 'hasSuggestedPrompt == false';

            return null;
        })
        .filter((predicate): predicate is string => predicate !== null);
}

function containsPredicates (field: string, values?: string[]): string[] {
    if (!Array.isArray(values)) return [];

    return values.map((value) =>
        (value === NONE_FILTER_VALUE ? `isEmpty(${field})` : `contains(${field}, "${escapeValue(value)}")`),
    );
}

function tokenRangePredicate (field: string, range?: RangeFilter): string | null {
    if (!range) return null;
    const parts: string[] = [];
    if (typeof range.min === 'number') parts.push(`${field} >= ${range.min}`);
    if (typeof range.max === 'number') parts.push(`${field} <= ${range.max}`);
    if (parts.length === 0) return null;

    return parts.join(' && ');
}

// Filter keys are `<propertyType>:<field>`, matching the filter bar's
// EVENT_PROPERTY_TYPES. A bare key (no colon) is treated as a custom property.
function parseCustomPropertyKey (mapKey: string): { propertyType: string; field: string } {
    const colonIndex = mapKey.indexOf(':');
    if (colonIndex === -1) return { propertyType: 'custom', field: mapKey };

    return { propertyType: mapKey.slice(0, colonIndex), field: mapKey.slice(colonIndex + 1) };
}

function customPropertyPredicate (mapKey: string, value: string): string {
    const { propertyType, field } = parseCustomPropertyKey(mapKey);
    const escaped = escapeValue(value);

    // Location data (country, region) are top-level scalar columns on the
    // conversation row, not entries in the `properties` map.
    if (propertyType === 'location') {
        return `${field} == "${escaped}"`;
    }

    // Custom event properties live on the row's `properties` map as arrays
    // (one entry per matching event), so match with contains(), not ==.
    return `contains(properties.${field}, "${escaped}")`;
}

function customPropertyFilterStages (filters?: Record<string, string[]>): c.PipelineData[] {
    if (!filters) return [];

    return Object.entries(filters)
        .map(([keyName, values]): c.PipelineData | null => {
            if (!Array.isArray(values) || values.length === 0) return null;
            const predicates = values.map((value) => customPropertyPredicate(keyName, value));
            const expression = joinOr(predicates);

            return expression ? o.filter(expression) : null;
        })
        .filter((stage): stage is c.PipelineData => stage !== null);
}

export function buildMetadataFilterStages (filters: MetadataFiltersInput): c.PipelineData[] {
    const stages: c.PipelineData[] = [
        ...applyOrFilter(fileUploadedPredicates(filters.fileUploadedFilter)),
        ...applyOrFilter(userReactionPredicates(filters.userReactionFilter)),
        ...applyOrFilter(usedSuggestedPromptPredicates(filters.usedSuggestedPromptFilter)),
        ...applyOrFilter(containsPredicates('toolsUsed', filters.toolsUsedFilter)),
        ...applyOrFilter(containsPredicates('modelsUsed', filters.modelsUsedFilter)),
        ...applyOrFilter(containsPredicates('subagentsUsed', filters.subagentsUsedFilter)),
    ];

    const inputRange = tokenRangePredicate('inputTokenCount', filters.inputTokenCountFilter);
    if (inputRange) stages.push(o.filter(inputRange));

    const outputRange = tokenRangePredicate('outputTokenCount', filters.outputTokenCountFilter);
    if (outputRange) stages.push(o.filter(outputRange));

    stages.push(...customPropertyFilterStages(filters.customPropertyFilters));

    return stages;
}

export function hasAnyMetadataFilter (filters: MetadataFiltersInput): boolean {
    return buildMetadataFilterStages(filters).length > 0;
}

export function conversationFilterByMetadataStages (
    filters: MetadataFiltersInput,
    baseParams: ConversationEventsBaseParams,
    sourceOptions: ConversationEventsSourceOptions = {},
): c.PipelineData[] {
    if (!hasAnyMetadataFilter(filters)) {
        return [];
    }

    const toolsValues = Array.isArray(filters.toolsUsedFilter) ? filters.toolsUsedFilter : [];
    const hasToolsFilter = toolsValues.length > 0;
    const stages: c.PipelineData[] = [];

    const nonToolsFilters: MetadataFiltersInput = hasToolsFilter
        ? { ...filters, toolsUsedFilter: [] }
        : filters;
    const hasNonToolsFilter = hasAnyMetadataFilter(nonToolsFilters);

    const nonToolsBranch = (): c.PipelineData[] => o.pipeline(
        ...buildMetadataFilterStages(nonToolsFilters),
        o.group(['conversationId'], o.groupField('_convMatchesMetadataFilters', o.count(null))),
    );
    const toolsExpression = joinOr(containsPredicates('toolsUsed', toolsValues));
    const toolsBranch = (): c.PipelineData[] => o.pipeline(
        o.filter(toolsExpression),
        o.group(['conversationId'], o.groupField('_convMatchesToolsFilter', o.count(null))),
    );

    if (hasNonToolsFilter && hasToolsFilter) {
        stages.push(
            o.merge(
                ['conversationId'],
                o.mappings({
                    _convMatchesMetadataFilters: '_convMatchesMetadataFilters',
                    _convMatchesToolsFilter: '_convMatchesToolsFilter',
                }),
                o.pipeline(
                    ...baseConversationEventsPipeline(baseParams, sourceOptions),
                    o.fork(nonToolsBranch(), toolsBranch()),
                    o.join(['conversationId']),
                ),
            ),
            o.filter('_convMatchesMetadataFilters > 0'),
            // Left-outer merge leaves non-matching rows null; in this DSL null > 0 is null, not false.
            o.filter('!isNil(_convMatchesMetadataFilters)'),
        );
    } else if (hasNonToolsFilter) {
        stages.push(
            o.merge(
                ['conversationId'],
                o.mappings({ _convMatchesMetadataFilters: '_convMatchesMetadataFilters' }),
                o.pipeline(
                    ...baseConversationEventsPipeline(baseParams, sourceOptions),
                    nonToolsBranch(),
                ),
            ),
            o.filter('_convMatchesMetadataFilters > 0'),
            o.filter('!isNil(_convMatchesMetadataFilters)'),
        );
    } else if (hasToolsFilter) {
        stages.push(
            o.merge(
                ['conversationId'],
                o.mappings({ _convMatchesToolsFilter: '_convMatchesToolsFilter' }),
                o.pipeline(
                    ...baseConversationEventsPipeline(baseParams, sourceOptions),
                    toolsBranch(),
                ),
            ),
        );
    }

    if (hasToolsFilter) {
        // Missing capability check via singleEvents for non-NONE filter values.
        // "get_additional_tools" is the virtual MCP tool name for all missing_capability
        // events — selecting it matches any conversation with a missing_capability trace.
        const MISSING_CAP_TOOL_NAME = 'get_additional_tools';
        const nonNoneValues = toolsValues.filter((value) => value !== NONE_FILTER_VALUE);
        if (nonNoneValues.length > 0) {
            const hasMissingCapFilter = nonNoneValues.includes(MISSING_CAP_TOOL_NAME);
            const capFilterExpr = hasMissingCapFilter
                ? 'agentTraceType == "missing_capability"'
                : `agentTraceType == "missing_capability" && (${nonNoneValues.map((value) => `contains(agentToolsConsidered, "${escapeValue(value)}")`).join(' || ')})`;
            stages.push(
                o.merge(
                    ['conversationId'],
                    o.mappings({ _convMatchesMissingCap: '_convMatchesMissingCap' }),
                    o.pipeline(
                        o.sources.singleEvents({
                            timeSeries: baseParams.timeSeries,
                            blacklist: baseParams.blacklist,
                            appId: baseParams.appId,
                            eventClass: ['agentic'],
                            ...(baseParams.experimentVersion ? { _eventVersion: baseParams.experimentVersion } : {}),
                        }),
                        o.filter(`agentId == "${baseParams.aiAgentId}"`),
                        o.filter(capFilterExpr),
                        o.group(['conversationId'], o.groupField('_convMatchesMissingCap', o.count(null))),
                    ),
                ),
                o.filter('!isNil(_convMatchesToolsFilter) || !isNil(_convMatchesMissingCap)'),
            );
        } else {
            stages.push(o.filter('!isNil(_convMatchesToolsFilter)'));
        }
    }

    return stages;
}

export type ConversationToolsConfig = MetadataFiltersInput & {
    appId: string;
    aiAgentId: string;
    timeSeries: c.TimeSeries;
    segmentId: string | null;
    selectedAccount?: c.AccountFilterAggInput;
    blacklist: c.BlacklistTypes;
    includeAnonymous: string;
    selectedTrackedUseCaseIds?: string[];
    selectedAgentStepRuleIds?: string[];
    experimentVersion?: string;
};

// Visitor/segment/account scoping plus the conversation-scoped TUC and metadata
// merges. These are keyed by conversationId, so they give the same verdict
// regardless of which event source feeds them — safe to apply to every branch.
export function conversationScopingStages (config: ConversationToolsConfig): c.PipelineData[] {
    return [
        o.identified(config.includeAnonymous),
        o.segment(config.segmentId),
        ...(config.selectedAccount ? [o.accountFilter(config.selectedAccount)] : []),
        ...trackedItemConversationScopedFilterStages({
            selectedTrackedUseCaseIds: config.selectedTrackedUseCaseIds ?? [],
            selectedAgentStepRuleIds: config.selectedAgentStepRuleIds ?? [],
            timeSeries: config.timeSeries,
            appId: config.appId,
            aiAgentId: config.aiAgentId,
            blacklist: config.blacklist,
            includeAnonymous: config.includeAnonymous,
            segmentId: config.segmentId,
            selectedAccount: config.selectedAccount,
            experimentVersion: config.experimentVersion,
        }),
        ...conversationFilterByMetadataStages(config as MetadataFiltersInput, {
            appId: config.appId,
            aiAgentId: config.aiAgentId,
            timeSeries: config.timeSeries,
            segmentId: config.segmentId,
            selectedAccount: config.selectedAccount,
            blacklist: config.blacklist,
            includeAnonymous: config.includeAnonymous,
            selectedTrackedUseCaseIds: config.selectedTrackedUseCaseIds,
            selectedAgentStepRuleIds: config.selectedAgentStepRuleIds,
            experimentVersion: config.experimentVersion,
        }),
    ].filter(Boolean) as c.PipelineData[];
}

// Normalize a single toolsUsed entry (string or { name }) to a string tool name, or null.
export const TOOL_USED_NAME_EXPRESSION =
    'if(isString(toolUsed), toolUsed, if(isNil(toolUsed.name), null, toolUsed.name))';

export const VALID_TOOL_USED_FILTER = 'isString(toolUsed) && !isEmpty(toolUsed)';

// LLM trace spans can carry noisy/empty toolsUsed values; only count real tool invocations.
// Built lazily: this module is imported from ai-agents.utils while constants may still be partial.
export function traceToolUsageFilter (): string {
    return `(type == "${MESSAGE_TYPES.API_TRACE}" && !isEmpty(toolsUsed) && (agentTraceType == "tool_request" || agentTraceType == "tool_response" || agentTraceType == "subagent_request")) || agentTraceType == "missing_capability"`;
}

function normalizeUnwoundToolUsedStages (): c.PipelineData[] {
    return [o.evaluate({ toolUsed: TOOL_USED_NAME_EXPRESSION }), o.filter(VALID_TOOL_USED_FILTER)];
}

// agent_response tools (web + API) from agenticEvents, collected per conversation.
// agenticEvents scopes to the agent at the source and contains every agent_response,
// including API-sent ones that never reach singleEvents.
export function responseToolsBranch (config: ConversationToolsConfig): c.PipelineData[] {
    return o.pipeline(
        o.sources.agenticEvents({
            timeSeries: config.timeSeries,
            blacklist: config.blacklist,
            appId: config.appId,
            agentId: config.aiAgentId,
            eventClass: ['agentic'],
            ...(config.experimentVersion ? { _eventVersion: config.experimentVersion } : {}),
        }),
        ...conversationScopingStages(config),
        o.filter(`${MESSAGE_TYPE_FILTERS.SYSTEM} && !isEmpty(toolsUsed)`),
        o.evaluate({ toolUsed: 'toolsUsed' }),
        o.unwind('toolUsed'),
        ...normalizeUnwoundToolUsedStages(),
        o.group(['conversationId'], o.groupField('responseTools', o.list('toolUsed'))),
    );
}

// agent_trace tools (and missing-capability tool names) from singleEvents, collected
// per conversation. Regular tool calls come from tool_request spans; missing_capability
// spans carry the attempted-but-unavailable tool names in agentToolsConsidered.
// singleEvents does not scope by agentId at the source, so apply it explicitly.
export function traceToolsBranch (config: ConversationToolsConfig): c.PipelineData[] {
    return o.pipeline(
        o.sources.singleEvents({
            timeSeries: config.timeSeries,
            blacklist: config.blacklist,
            appId: config.appId,
            eventClass: ['agentic'],
            ...(config.experimentVersion ? { _eventVersion: config.experimentVersion } : {}),
        }),
        o.filter(`agentId == "${config.aiAgentId}"`),
        ...conversationScopingStages(config),
        o.filter(traceToolUsageFilter()),
        o.evaluate({ toolUsed: 'if(agentTraceType == "missing_capability", "get_additional_tools", toolsUsed)' }),
        o.unwind('toolUsed'),
        ...normalizeUnwoundToolUsedStages(),
        o.group(['conversationId'], o.groupField('traceTools', o.list('toolUsed'))),
    );
}

export function conversationToolsBranch (config: ConversationToolsConfig): c.PipelineData[] {
    return o.pipeline(
        o.sources.agenticConversationEvents({
            timeSeries: config.timeSeries,
            blacklist: config.blacklist,
            appId: config.appId,
            ...(config.experimentVersion ? { _eventVersion: config.experimentVersion } : {}),
        }),
        o.filter(`agentId == "${config.aiAgentId}"`),
        ...conversationScopingStages(config),
        o.filter('!isEmpty(toolsUsed)'),
        o.evaluate({ toolUsed: 'toolsUsed' }),
        o.unwind('toolUsed'),
        ...normalizeUnwoundToolUsedStages(),
        o.group(['conversationId'], o.groupField('conversationTools', o.list('toolUsed'))),
    );
}

export const TOOLS_USED_COALESCE_EXPRESSION =
    'if(!isEmpty(traceTools), traceTools, if(!isEmpty(responseTools), responseTools, conversationTools))';

// Rows of { conversationId, toolUsed: [<distinct-ish tool list>] } — one per conversation
// that used at least one tool. No dropPartial on the join: a conversation seen by only
// one source (e.g. trace-only) must survive for the coalesce to pick it up.
export function toolsPerConversationStages (config: ConversationToolsConfig): c.PipelineData[] {
    return [
        o.spawn(responseToolsBranch(config), traceToolsBranch(config), conversationToolsBranch(config)),
        o.join('conversationId'),
        o.evaluate({ toolUsed: TOOLS_USED_COALESCE_EXPRESSION }),
    ];
}

export function getPeriodForGrouping (timeSeries: c.TimeSeries, selectedPeriod?: { id: string }): string {
    if (selectedPeriod?.id && ['hourly', 'daily', 'weekly', 'monthly'].includes(selectedPeriod.id)) {
        return selectedPeriod.id;
    }

    const period = timeSeries?.period ?? 'dayRange';

    if (period === 'monthly' || period === 'monthRange') {
        return 'monthly';
    }

    if (period === 'weekly' || period === 'weekRange') {
        return 'weekly';
    }

    if (period === 'hourly' || period === 'hourRange') {
        return 'hourly';
    }

    return 'daily';
}

export function trackedIntentMetricPeriodStartEvaluateStage (
    timeSeries: c.TimeSeries,
    selectedPeriod?: { id: string },
): c.PipelineData {
    const periodType = getPeriodForGrouping(timeSeries, selectedPeriod);

    return o.evaluate({
        periodStart: `startOfPeriod("${periodType}", first)`,
    });
}

export function trackedIntentMetricPeriodFilterStages (
    timeSeries: c.TimeSeries,
    selectedPeriod?: { id: string },
    periodStart?: number,
): c.PipelineData[] {
    if (periodStart == null) {
        return [];
    }

    return [
        trackedIntentMetricPeriodStartEvaluateStage(timeSeries, selectedPeriod),
        o.filter(`periodStart == ${periodStart}`),
    ];
}

export function retentionStages (label: string): c.PipelineData[] {
    return [
        o.group(['visitorId', 'weekStart'], o.groupField('visitsInWeek', o.count('first'))),
        o.group(
            ['visitorId'],
            o.groupField('uniqueWeeks', o.count('weekStart')),
            o.groupField('totalVisits', o.sum('visitsInWeek')),
        ),
        o.evaluate({
            isRetained: 'uniqueWeeks >= 2',
        }),
        o.group(
            [],
            o.groupField('totalVisitors', o.count('visitorId')),
            o.groupField('retainedVisitors', o.countIf('visitorId', 'isRetained')),
        ),
        o.evaluate({
            retentionRate: 'retainedVisitors / totalVisitors * 100',
            beginningOfCohort: 'startOfPeriod("weekly", dateAdd(now(), -2, "weeks"))',
            relativePeriods: '0',
            retention: '{label: "00-00"}',
        }),
        o.select({
            [label]:
                '{beginningOfCohort, relativePeriods, visitors: totalVisitors, retention, retained: retainedVisitors, retentionRate}',
        }),
    ];
}

/**
 * Builds ragePromptConversationIds for tracked use case rows by matching TUC
 * eventReferences to prompt-level rage on agenticEvents (V1 use-case scoping).
 * TUC subrow ragePromptCount is often unset while prompt ragePrompt is present.
 */
export function trackedUseCaseRagePromptConversationIdsPipeline (
    config: ConversationEventsBaseParams & { selectedAccount?: c.AccountFilterAggInput },
    groupFields: string[],
): c.PipelineData[] {
    const {
        appId,
        aiAgentId,
        timeSeries,
        segmentId,
        selectedAccount,
        blacklist,
        includeAnonymous,
        experimentVersion,
    } = adjustedConversationParams(config);
    const ragePromptValueFilter = '!isNil(ragePrompt) && ragePrompt != "None" && ragePrompt != ""';

    return o.pipeline(
        o.filter('!isNull(eventReferences) && len(eventReferences) > 0'),
        o.evaluate({ eventId: 'eventReferences' }),
        o.unwind('eventId', { keepEmpty: false }),
        o.merge(
            ['eventId'],
            o.mappings({ ragePrompt: 'ragePrompt' }),
            o.pipeline(
                o.sources.agenticEvents({
                    timeSeries,
                    blacklist,
                    appId,
                    agentId: aiAgentId,
                    ...(experimentVersion ? { _eventVersion: experimentVersion } : {}),
                }),
                o.identified(includeAnonymous),
                segmentId ? o.segment(segmentId) : null,
                selectedAccount ? o.accountFilter(selectedAccount) : null,
                o.filter(RAGE_PROMPT_USER_FILTER),
            ),
        ),
        o.filter(ragePromptValueFilter),
        o.group([...groupFields, 'conversationId']),
        o.group(
            groupFields,
            o.groupField('ragePromptConversationIds', o.listUnique('conversationId')),
        ),
    );
}
